import { t } from '../i18n';

// ============================================================
// Простой автокомплит для текстового поля (по образцу игроков в форме игры):
// выпадающий список совпадений, стрелки/Enter/Escape, выбор по клику.
// ============================================================

export interface AutocompleteOptions {
    /** Текст пустого результата. */
    emptyText?: string;
    /** Максимум вариантов в списке. */
    limit?: number;
}

/**
 * Оборачивает input в .player-autocomplete и вешает выпадашку.
 * После выбора значения input получает событие input (для автосохранения).
 */
export function attachAutocomplete(
    input: HTMLInputElement,
    getItems: () => string[],
    options: AutocompleteOptions = {}
): void {
    const parent = input.parentNode;
    if (!parent) return;

    const limit = options.limit ?? 10;
    const emptyText = options.emptyText ?? t('maps.author_not_found');

    input.autocomplete = 'off';

    const wrapper = document.createElement('div');
    wrapper.className = 'player-autocomplete';
    parent.insertBefore(wrapper, input);
    wrapper.appendChild(input);

    const dropdown = document.createElement('div');
    dropdown.className = 'player-autocomplete-dropdown hidden';
    wrapper.appendChild(dropdown);

    let matches: string[] = [];
    let highlighted = -1;
    // После выбора не показываем список повторно на синтетическом input
    let justSelected = false;

    function hide(): void {
        dropdown.classList.add('hidden');
        highlighted = -1;
    }

    function select(value: string): void {
        input.value = value;
        justSelected = true;
        hide();
        input.dispatchEvent(new Event('input', { bubbles: true }));
        justSelected = false;
    }

    function show(query: string): void {
        const q = query.trim().toLowerCase();
        if (!q) { matches = []; hide(); return; }
        matches = getItems()
            .filter((a) => a.toLowerCase().includes(q))
            .slice(0, limit);

        dropdown.innerHTML = '';
        if (matches.length === 0) {
            const empty = document.createElement('div');
            empty.className = 'player-autocomplete-empty';
            empty.textContent = emptyText;
            dropdown.appendChild(empty);
            dropdown.classList.remove('hidden');
            return;
        }

        matches.forEach((value, i) => {
            const item = document.createElement('div');
            item.className = 'player-autocomplete-item';
            if (i === highlighted) item.classList.add('is-highlighted');
            const name = document.createElement('span');
            name.className = 'player-autocomplete-name';
            name.textContent = value;
            item.appendChild(name);
            item.addEventListener('mousedown', (e) => {
                e.preventDefault();
                select(value);
            });
            dropdown.appendChild(item);
        });
        dropdown.classList.remove('hidden');
    }

    input.addEventListener('input', () => {
        if (justSelected) return;
        show(input.value);
    });

    input.addEventListener('focus', () => {
        if (input.value.trim()) show(input.value);
    });

    input.addEventListener('blur', () => {
        setTimeout(hide, 150);
    });

    input.addEventListener('keydown', (e) => {
        if (dropdown.classList.contains('hidden')) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            highlighted = Math.min(highlighted + 1, matches.length - 1);
            show(input.value);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            highlighted = Math.max(highlighted - 1, -1);
            show(input.value);
        } else if (e.key === 'Enter') {
            if (highlighted >= 0) {
                e.preventDefault();
                const value = matches[highlighted];
                if (value) select(value);
            }
        } else if (e.key === 'Escape') {
            hide();
        }
    });
}
